import type { ResourceAvailability } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  NoPermissionState,
  Notice,
  PageHeader,
  Select,
  Skeleton,
} from '@horevia/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Ban, CalendarCheck, CalendarSearch, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { BlockFormDrawer } from '@/features/resources/BlockFormDrawer';
import {
  availabilityQueryOptions,
  resourceTypesQueryOptions,
  resourcesQueryOptions,
  type AvailabilitySearch,
} from '@/features/resources/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/resources/availability')({
  component: AvailabilityPage,
});

/** `yyyy-MM-dd` local, pour les champs `<input type="date">`. */
function dateInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toIso(date: string, time: string): string {
  return new Date(`${date}T${time || '00:00'}:00`).toISOString();
}

function AvailabilityPage() {
  const { t, i18n } = useTranslation([
    'availability',
    'resources',
    'common',
    'errors',
    'maintenance',
  ]);
  const canRead = useCan('availability.read');
  const canBlock = useCan('availability.block');

  const today = new Date();
  const tomorrow = new Date(today.getTime() + 86_400_000);

  const [form, setForm] = useState({
    fromDate: dateInput(today),
    fromTime: '12:00',
    toDate: dateInput(tomorrow),
    toTime: '12:00',
    typeCode: '',
    capacity: '',
  });
  const [submitted, setSubmitted] = useState<AvailabilitySearch | null>(null);
  const [blockDrawer, setBlockDrawer] = useState<{
    open: boolean;
    preset?: { resourceId?: string; startsAt?: string; endsAt?: string };
  }>({ open: false });

  const typesQuery = useQuery({ ...resourceTypesQueryOptions(), enabled: canRead });
  const resourcesQuery = useInfiniteQuery({
    ...resourcesQueryOptions({}),
    enabled: canBlock,
  });
  const query = useQuery({
    ...availabilityQueryOptions(submitted ?? { from: '', to: '' }, Boolean(submitted) && canRead),
  });

  const rangeInvalid = useMemo(() => {
    const from = new Date(toIso(form.fromDate, form.fromTime)).getTime();
    const to = new Date(toIso(form.toDate, form.toTime)).getTime();
    return !(to > from);
  }, [form]);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const results = query.data ?? [];
  const availableCount = results.filter((item) => item.available).length;
  const nights =
    submitted !== null
      ? Math.max(
          1,
          Math.round(
            (new Date(submitted.to).getTime() - new Date(submitted.from).getTime()) / 86_400_000,
          ),
        )
      : 0;

  const submit = () => {
    if (rangeInvalid) return;
    setSubmitted({
      from: toIso(form.fromDate, form.fromTime),
      to: toIso(form.toDate, form.toTime),
      ...(form.typeCode ? { typeCode: form.typeCode } : {}),
      ...(form.capacity ? { capacity: Number(form.capacity) } : {}),
    });
  };

  return (
    <Container>
      <PageHeader
        title={t('availability:title')}
        subtitle={t('availability:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('resources:title'),
            link: <Link to="/resources">{t('resources:title')}</Link>,
          },
          { label: t('availability:title') },
        ]}
      />

      <div className="stack">
        <Card>
          <CardHeader
            title={t('availability:form.title')}
            icon={<CalendarSearch className="ico ico-16" aria-hidden="true" />}
          />
          <CardBody>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <div className="field-row-3">
                <FormField
                  label={t('availability:form.from')}
                  required
                  requiredLabel={t('common:fields.required')}
                >
                  {(field) => (
                    <Input
                      id={field.id}
                      type="date"
                      value={form.fromDate}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, fromDate: event.target.value }))
                      }
                    />
                  )}
                </FormField>
                <FormField label={t('availability:form.fromTime')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      type="time"
                      value={form.fromTime}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, fromTime: event.target.value }))
                      }
                    />
                  )}
                </FormField>
                <FormField label={t('availability:form.type')}>
                  {(field) => (
                    <Select
                      id={field.id}
                      value={form.typeCode || 'ALL'}
                      onValueChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          typeCode: value === 'ALL' ? '' : value,
                        }))
                      }
                      options={[
                        { value: 'ALL', label: t('availability:form.anyType') },
                        ...(typesQuery.data ?? []).map((type) => ({
                          value: type.code,
                          label: type.name,
                        })),
                      ]}
                    />
                  )}
                </FormField>
              </div>

              <div className="field-row-3 mt-4">
                <FormField
                  label={t('availability:form.to')}
                  required
                  requiredLabel={t('common:fields.required')}
                  error={rangeInvalid ? t('availability:invalidRange') : undefined}
                >
                  {(field) => (
                    <Input
                      id={field.id}
                      type="date"
                      invalid={field.invalid}
                      value={form.toDate}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, toDate: event.target.value }))
                      }
                    />
                  )}
                </FormField>
                <FormField label={t('availability:form.toTime')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      type="time"
                      value={form.toTime}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, toTime: event.target.value }))
                      }
                    />
                  )}
                </FormField>
                <FormField
                  label={t('availability:form.capacity')}
                  hint={t('availability:form.capacityHint')}
                >
                  {(field) => (
                    <Input
                      id={field.id}
                      type="number"
                      min={1}
                      max={60}
                      value={form.capacity}
                      aria-describedby={field.describedBy}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, capacity: event.target.value }))
                      }
                    />
                  )}
                </FormField>
              </div>

              <div className="row mt-4" style={{ gap: 8 }}>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={rangeInvalid}
                  loading={query.isFetching}
                >
                  <Search className="ico" aria-hidden="true" />
                  {t('availability:search')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSubmitted(null);
                    setForm({
                      fromDate: dateInput(today),
                      fromTime: '12:00',
                      toDate: dateInput(tomorrow),
                      toTime: '12:00',
                      typeCode: '',
                      capacity: '',
                    });
                  }}
                >
                  {t('availability:form.reset')}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>

        {submitted === null ? (
          <Card>
            <EmptyState
              icon={<CalendarSearch className="ico ico-24" aria-hidden="true" />}
              title={t('availability:emptyTitle')}
              description={t('availability:emptyBody')}
            />
          </Card>
        ) : query.isPending ? (
          <Card>
            <CardBody className="stack">
              <Skeleton height={64} />
              <Skeleton height={64} />
              <Skeleton height={64} />
            </CardBody>
          </Card>
        ) : query.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(query.error, t)}
            traceId={errorTraceId(query.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void query.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : results.length === 0 ? (
          <Card>
            <EmptyState
              icon={<CalendarSearch className="ico ico-24" aria-hidden="true" />}
              title={t('availability:noResultTitle')}
              description={t('availability:noResultBody')}
            />
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={t('availability:results.title')}
              icon={<CalendarCheck className="ico ico-16" aria-hidden="true" />}
              iconTone="green"
              actions={
                <>
                  <Badge tone="green">
                    {t('availability:results.availableCount', { count: availableCount })}
                  </Badge>
                  <Badge tone="neutral">
                    {t('availability:results.unavailableCount', {
                      count: results.length - availableCount,
                    })}
                  </Badge>
                </>
              }
            />
            <CardBody className="stack stack--sm">
              {results.map((item) => (
                <AvailabilityRow
                  key={item.resourceId}
                  item={item}
                  nights={nights}
                  canBlock={canBlock}
                  onBlock={() =>
                    setBlockDrawer({
                      open: true,
                      preset: {
                        resourceId: item.resourceId,
                        startsAt: submitted.from,
                        endsAt: submitted.to,
                      },
                    })
                  }
                  locale={i18n.language}
                />
              ))}
            </CardBody>
          </Card>
        )}

        <Notice tone="violet" title={t('resources:compositeNotice.title')}>
          <p className="mb-0 t-13">{t('resources:compositeNotice.body')}</p>
        </Notice>
      </div>

      <BlockFormDrawer
        open={blockDrawer.open}
        onOpenChange={(open) => setBlockDrawer((current) => ({ ...current, open }))}
        resources={resourcesQuery.data?.pages.flatMap((page) => page.data) ?? []}
        preset={blockDrawer.preset}
      />
    </Container>
  );
}

interface AvailabilityRowProps {
  item: ResourceAvailability;
  nights: number;
  canBlock: boolean;
  locale: string;
  onBlock: () => void;
}

function AvailabilityRow({ item, nights, canBlock, locale, onBlock }: AvailabilityRowProps) {
  const { t } = useTranslation(['availability', 'resources', 'common']);
  const rate = item.defaultRate;

  return (
    <div
      className={`availability-row ${item.available ? 'availability-row--free' : 'availability-row--busy'}`}
    >
      <div className="availability-row__body">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Link
            to="/resources/$resourceId"
            params={{ resourceId: item.resourceId }}
            className="strong"
          >
            {item.resourceName}
          </Link>
          <Badge tone={item.available ? 'green' : 'neutral'} dot>
            {item.available
              ? t('availability:results.available')
              : t('availability:results.unavailable')}
          </Badge>
          {item.parentResourceId ? <Badge tone="neutral">{t('resources:card.child')}</Badge> : null}
        </div>

        <div className="t-12 muted mt-1">
          {item.typeCode} ·{' '}
          {t('resources:card.capacity', { standard: item.capacityMax, max: item.capacityMax })}
          {rate
            ? ` · ${formatAmount(rate.price, locale, rate.currencyCode)} ${t(`resources:rates.perUnit.${rate.pricingUnit}`, { defaultValue: '' })}`
            : ''}
        </div>

        {item.available && rate ? (
          <div className="t-13 mt-1">
            {t('availability:results.nights', { count: nights })} ·{' '}
            {t('availability:results.estimate', {
              amount: formatAmount(rate.price * nights, locale, rate.currencyCode),
            })}
          </div>
        ) : null}

        {item.conflicts.length > 0 ? (
          <>
            <div className="panel-title mt-2">{t('availability:results.conflictsTitle')}</div>
            <ul className="availability-conflicts">
              {item.conflicts.map((conflict) => {
                const originIsSelf = conflict.resourceId === item.resourceId;
                const originIsParent = conflict.resourceId === item.parentResourceId;
                const explanation = originIsSelf
                  ? t('availability:results.conflictSelf', { resource: conflict.resourceName })
                  : originIsParent
                    ? t('availability:results.conflictParent', { resource: conflict.resourceName })
                    : t('availability:results.conflictChild', { resource: conflict.resourceName });
                return (
                  <li key={`${conflict.sourceId}-${conflict.unitCode}`}>
                    <strong>
                      {conflict.source === 'RESERVATION'
                        ? t('availability:results.conflictReservation', {
                            resource: conflict.resourceName,
                          })
                        : t('availability:results.conflictBlock', {
                            resource: conflict.resourceName,
                          })}
                    </strong>{' '}
                    {t('availability:results.conflictFrom', {
                      from: formatDateTime(conflict.startsAt, locale),
                      to: formatDateTime(conflict.endsAt, locale),
                    })}{' '}
                    · {t('availability:results.unit', { code: conflict.unitCode })}
                    <div className="muted">{explanation}</div>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}
      </div>

      <div className="row" style={{ gap: 6 }}>
        <Button variant="secondary" size="sm" disabled title={t('availability:results.book')}>
          {t('availability:results.book')}
        </Button>
        {canBlock ? (
          <Button variant="ghost" size="sm" onClick={onBlock}>
            <Ban className="ico ico-14" aria-hidden="true" />
            {t('availability:results.block')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
